// Contratos laborales (nomina.contratos). Nómina → Personal.
// Tipos y validaciones según el Código del Trabajo (Arts. 11, 14-17, 47);
// la validación autoritativa vive en la RPC nom_contrato_guardar.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Calculator, Download, FileSignature, Loader2, Plus, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  Campo, Modal, descargarPdfBase64, fieldCls, money, fecha, nom, nomRpc,
  TIPOS_CONTRATO, FORMAS_BENEFICIO, useDialogoEnLinea,
  type Cargo, type ContratoBajoSbu, type ContratoRow, type Departamento, type EmpleadoRow, hoyISO } from './shared';
import { SimuladorCostoModal } from './SimuladorCosto';

const vacio = {
  empleado_id: '', tipo: 'indefinido', fecha_inicio: '', fecha_fin: '',
  periodo_prueba_dias: '0', jornada: 'completa', horas_semanales: '40', modalidad: 'presencial',
  departamento_id: '', cargo_id: '', sueldo: '',
  decimo_tercero: 'acumulado', decimo_cuarto: 'acumulado', fondos_reserva: 'acumulado', observaciones: '',
};
type Form = typeof vacio;
const REQUIEREN_FIN = ['eventual', 'ocasional', 'temporada'];

export function Contratos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('vigente');
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<ContratoRow[]>({
    queryKey: ['nom-contratos', estado],
    queryFn: () => nomRpc<ContratoRow[]>('nom_contratos', { _empleado_id: null, _estado: estado }),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.pdf_estado === 'pendiente' || r.pdf_estado === 'generando') ? 4000 : false,
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: false }),
  });
  const { data: departamentos = [] } = useQuery<Departamento[]>({
    queryKey: ['nom-departamentos'],
    queryFn: () => nom.list<Departamento>('departamentos', '?select=*&deleted_at=is.null&order=nombre'),
  });
  const { data: cargos = [] } = useQuery<Cargo[]>({
    queryKey: ['nom-cargos'],
    queryFn: () => nom.list<Cargo>('cargos', '?select=*&deleted_at=is.null&order=nombre'),
  });

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t) || (r.codigo ?? '').toLowerCase().includes(t));
  }, [rows, q]);

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<Form>(vacio);
  const [terminando, setTerminando] = useState<ContratoRow | null>(null);
  const [fechaTerm, setFechaTerm] = useState('');
  const [motivoTerm, setMotivoTerm] = useState('');

  const setCargo = (cargo_id: string) => {
    const c = cargos.find((x) => x.id === cargo_id);
    setForm((f) => ({ ...f, cargo_id, sueldo: f.sueldo || (c?.sueldo_referencial != null ? String(c.sueldo_referencial) : '') }));
  };

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_contrato_guardar', {
      _data: {
        empleado_id: form.empleado_id, tipo: form.tipo,
        fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin || null,
        periodo_prueba_dias: Number(form.periodo_prueba_dias) || 0,
        jornada: form.jornada, horas_semanales: Number(form.horas_semanales) || 40,
        modalidad: form.modalidad,
        departamento_id: form.departamento_id || null, cargo_id: form.cargo_id || null,
        sueldo: Number(form.sueldo),
        decimo_tercero: form.decimo_tercero, decimo_cuarto: form.decimo_cuarto, fondos_reserva: form.fondos_reserva,
        observaciones: form.observaciones || null,
      },
    }),
    onSuccess: () => {
      toast.success('Contrato guardado'); setAbierto(false);
      void qc.invalidateQueries({ queryKey: ['nom-contratos'] });
      void qc.invalidateQueries({ queryKey: ['nom-empleados'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const generarPdf = useMutation({
    mutationFn: (id: string) => nomRpc('nom_contrato_generar_pdf', { _contrato_id: id }),
    onSuccess: () => { toast.success('Contrato encolado para firma electrónica'); void qc.invalidateQueries({ queryKey: ['nom-contratos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const descargarPdf = useMutation({
    mutationFn: (id: string) => nomRpc<{ pdf: string; filename: string } | null>('nom_contrato_pdf', { _contrato_id: id }),
    onSuccess: (d) => { if (d?.pdf) descargarPdfBase64(d.pdf, d.filename); else toast.error('PDF no disponible'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const terminar = useMutation({
    mutationFn: () => nomRpc('nom_contrato_terminar', { _id: terminando!.id, _fecha: fechaTerm, _motivo: motivoTerm || null }),
    onSuccess: () => {
      toast.success('Contrato terminado'); setTerminando(null);
      void qc.invalidateQueries({ queryKey: ['nom-contratos'] });
      void qc.invalidateQueries({ queryKey: ['nom-empleados'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const faltaFin = REQUIEREN_FIN.includes(form.tipo) && !form.fecha_fin;

  const headersExp = ['N°', 'Código', 'Empleado', 'Tipo', 'Cargo', 'Inicio', 'Fin', 'Jornada', 'Sueldo', 'Estado'];
  const MONEYCOLS = [8];
  const filasExport: Celda[][] = [
    ...filtrados.map((r, i): Celda[] => [
      i + 1, r.codigo, r.razon_social, (TIPOS_CONTRATO[r.tipo] ?? r.tipo).split(' (')[0], r.cargo ?? '—',
      fecha(r.fecha_inicio), fecha(r.fecha_fin),
      `${r.jornada === 'parcial' ? `Parcial ${Number(r.horas_semanales)}h` : 'Completa'}${r.modalidad === 'teletrabajo' ? ' · Tele' : ''}`,
      r.sueldo != null ? Number(r.sueldo) : '',
      r.estado === 'vigente' ? 'Vigente' : `Terminado ${fecha(r.fecha_terminacion)}`,
    ]),
    ['', '', '', '', '', '', '', 'TOTAL', filtrados.reduce((s, r) => s + (r.sueldo != null ? Number(r.sueldo) : 0), 0), ''],
  ];

  // Art. 81 CT: un contrato vigente se mide contra el básico DE HOY, no contra
  // el del año en que se firmó. Cada enero sube y los viejos quedan debajo sin
  // que nadie los toque (script 798).
  const [simulador, setSimulador] = useState(false);
  const { data: bajoSbu = [] } = useQuery<ContratoBajoSbu[]>({
    queryKey: ['nom-contratos-bajo-sbu'],
    queryFn: () => nomRpc<ContratoBajoSbu[]>('nom_contratos_bajo_sbu'),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Contratos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`contratos-${fechaLocal()}`}
            titulo="Contratos" headers={headersExp} filas={filasExport} colsDerecha={[0, 8]}
            disabled={filtrados.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <Button variant="outline" onClick={() => setSimulador(true)}>
            <Calculator className="w-4 h-4 mr-1" /> Simular costo
          </Button>
          <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nuevo contrato</Button>
        </div>
      </div>

      {bajoSbu.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
            <span>
              <b>{bajoSbu.length} contrato(s) vigentes por debajo del salario básico</b> proporcional a su jornada
              (Art. 81 CT). Subirlos cuesta {money(bajoSbu.reduce((t, c) => t + Number(c.costo_anual), 0))} al año,
              con aporte patronal y décimo tercero incluidos.
            </span>
          </div>
          <div className="max-h-40 overflow-y-auto text-xs space-y-1">
            {bajoSbu.map((c) => (
              <div key={c.contrato_id} className="flex flex-wrap justify-between gap-3 border-b border-border/40 pb-1 last:border-0">
                <span>{c.empleado} <span className="font-mono text-muted-foreground">{c.empleado_codigo}</span></span>
                <span className="tabular-nums">gana {money(c.sueldo)} · mínimo {money(c.minimo)} ({c.anio_ref})</span>
                <span className="tabular-nums text-destructive">faltan {money(c.faltante)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select className={`${fieldCls} w-44`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
          <option value="vigente">Vigentes</option>
          <option value="terminado">Terminados</option>
          <option value="todos">Todos</option>
        </select>
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre, cédula o código…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Contratos ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3">Cargo</th><th className="py-2 pr-3">Inicio</th><th className="py-2 pr-3">Fin</th>
              <th className="py-2 pr-3">Jornada</th><th className="py-2 pr-3 text-right">Sueldo</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">PDF firmado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && !isFetching && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin contratos</td></tr>}
              {filtrados.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.codigo}</td>
                  <td className="py-2 pr-3">{r.razon_social}<div className="text-xs text-muted-foreground font-mono">{r.identificacion}</div></td>
                  <td className="py-2 pr-3">{(TIPOS_CONTRATO[r.tipo] ?? r.tipo).split(' (')[0]}</td>
                  <td className="py-2 pr-3">{r.cargo ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(r.fecha_inicio)}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(r.fecha_fin)}</td>
                  <td className="py-2 pr-3">{r.jornada === 'parcial' ? `Parcial ${Number(r.horas_semanales)}h` : 'Completa'}{r.modalidad === 'teletrabajo' ? ' · Tele' : ''}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.sueldo)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${r.estado === 'vigente' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>
                      {r.estado === 'vigente' ? 'Vigente' : `Terminado ${fecha(r.fecha_terminacion)}`}
                    </span>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {r.pdf_estado === 'firmado' && (
                      <>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-completado-soft text-completado mr-1" title={`Firmado ${fecha(r.pdf_firmado_at)}`}>Firmado</span>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Descargar PDF firmado" onClick={() => descargarPdf.mutate(r.id)}><Download className="w-4 h-4" /></button>
                      </>
                    )}
                    {(r.pdf_estado === 'pendiente' || r.pdf_estado === 'generando') && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-pendiente-soft text-pendiente inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Firmando…</span>
                    )}
                    {r.pdf_estado === 'error' && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-urgente-soft text-urgente mr-1" title={r.pdf_error ?? ''}>Error</span>
                    )}
                    {(r.pdf_estado == null || r.pdf_estado === 'error' || r.pdf_estado === 'firmado') && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={r.pdf_estado === 'firmado' ? 'Regenerar y firmar de nuevo' : 'Generar PDF firmado (.p12)'} onClick={async () => { if (r.pdf_estado !== 'firmado' || (await dlg.confirmar('¿Regenerar el PDF?', { detalle: 'Reemplaza el firmado actual.', aceptar: 'Regenerar' }))) generarPdf.mutate(r.id); }}><FileSignature className="w-4 h-4" /></button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {r.estado === 'vigente' && (
                      <button
                        className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10"
                        title="Terminar contrato"
                        onClick={() => { setTerminando(r); setFechaTerm(hoyISO()); setMotivoTerm(''); }}
                      ><StopCircle className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nuevo contrato" onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Empleado *">
                <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                  <option value="">— Selecciona —</option>
                  {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
                </select>
              </Campo>
              <Campo label="Tipo de contrato *">
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value, periodo_prueba_dias: e.target.value === 'indefinido' ? form.periodo_prueba_dias : '0' })}>
                  {Object.entries(TIPOS_CONTRATO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Campo>
              <Campo label="Fecha de inicio *"><input type="date" className={fieldCls} value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></Campo>
              <Campo label={`Fecha de fin ${REQUIEREN_FIN.includes(form.tipo) ? '*' : '(opcional)'}`}>
                <input type="date" className={fieldCls} value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
              </Campo>
              <Campo label="Departamento">
                <select className={fieldCls} value={form.departamento_id} onChange={(e) => setForm({ ...form, departamento_id: e.target.value })}>
                  <option value="">—</option>{departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Cargo">
                <select className={fieldCls} value={form.cargo_id} onChange={(e) => setCargo(e.target.value)}>
                  <option value="">—</option>{cargos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Sueldo mensual ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={form.sueldo} onChange={(e) => setForm({ ...form, sueldo: e.target.value })} /></Campo>
              {form.tipo === 'indefinido' && (
                <Campo label="Período de prueba (días, máx. 90 — Art. 15 CT)">
                  <input type="number" min="0" max="90" className={fieldCls} value={form.periodo_prueba_dias} onChange={(e) => setForm({ ...form, periodo_prueba_dias: e.target.value })} />
                </Campo>
              )}
              <Campo label="Jornada (Art. 47 CT: máx. 40 h/semana)">
                <select className={fieldCls} value={form.jornada} onChange={(e) => setForm({ ...form, jornada: e.target.value, horas_semanales: e.target.value === 'completa' ? '40' : '20' })}>
                  <option value="completa">Completa (40 h)</option>
                  <option value="parcial">Parcial (&lt;40 h)</option>
                </select>
              </Campo>
              {form.jornada === 'parcial' && (
                <Campo label="Horas semanales"><input type="number" min="1" max="39" className={fieldCls} value={form.horas_semanales} onChange={(e) => setForm({ ...form, horas_semanales: e.target.value })} /></Campo>
              )}
              <Campo label="Modalidad">
                <select className={fieldCls} value={form.modalidad} onChange={(e) => setForm({ ...form, modalidad: e.target.value })}>
                  <option value="presencial">Presencial</option>
                  <option value="teletrabajo">Teletrabajo</option>
                </select>
              </Campo>
              <Campo label="Décimo tercero (Art. 111)">
                <select className={fieldCls} value={form.decimo_tercero} onChange={(e) => setForm({ ...form, decimo_tercero: e.target.value })}>
                  {Object.entries(FORMAS_BENEFICIO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Campo>
              <Campo label="Décimo cuarto (Art. 113)">
                <select className={fieldCls} value={form.decimo_cuarto} onChange={(e) => setForm({ ...form, decimo_cuarto: e.target.value })}>
                  {Object.entries(FORMAS_BENEFICIO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Campo>
              <Campo label="Fondos de reserva (Art. 196)">
                <select className={fieldCls} value={form.fondos_reserva} onChange={(e) => setForm({ ...form, fondos_reserva: e.target.value })}>
                  <option value="acumulado">Depósito en el IESS</option>
                  <option value="mensualizado">Mensualizado</option>
                </select>
              </Campo>
            </div>
            <Campo label="Observaciones"><input className={fieldCls} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></Campo>
            {faltaFin && <div className="text-xs text-urgente">Los contratos eventual/ocasional/temporada requieren fecha de fin (Art. 17 CT).</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !form.fecha_inicio || !Number(form.sueldo) || faltaFin} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {terminando && (
        <Modal title={`Terminar contrato ${terminando.codigo ?? ''}`} onClose={() => setTerminando(null)}>
          <div className="space-y-3">
            <div className="text-sm">{terminando.razon_social} — {(TIPOS_CONTRATO[terminando.tipo] ?? terminando.tipo).split(' (')[0]}</div>
            <Campo label="Fecha de terminación *"><input type="date" className={fieldCls} value={fechaTerm} onChange={(e) => setFechaTerm(e.target.value)} /></Campo>
            <Campo label="Motivo (Art. 169 CT)"><input className={fieldCls} value={motivoTerm} onChange={(e) => setMotivoTerm(e.target.value)} placeholder="Renuncia, desahucio, acuerdo entre las partes…" /></Campo>
            <div className="text-xs text-muted-foreground">La liquidación/acta de finiquito se gestionará en Salidas → Liquidaciones (fase posterior).</div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTerminando(null)}>Cancelar</Button>
              <Button variant="destructive" isLoading={terminar.isPending} disabled={!fechaTerm} onClick={() => terminar.mutate()}>Terminar contrato</Button>
            </div>
          </div>
        </Modal>
      )}
      {simulador && <SimuladorCostoModal onClose={() => setSimulador(false)} />}
    </div>
  );
}
